import React, { useCallback } from 'react';
import { ChipSelector } from './ChipSelector';
import DayFirstDate from "./DayFirstDate";

// ── Chip Options ───────────────────────────────────────────────────
const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' }, { value: 'female', label: 'Female' },
  { value: 'non_binary', label: 'Non-binary' }, { value: 'other', label: 'Other' },
  { value: 'prefer_not', label: 'Prefer not to say' },
];
const YES_NO = [
  { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' },
];
const YES_NO_UNSURE = [
  { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'unsure', label: 'Unsure' },
];
const YES_NO_DISCUSS = [
  { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'discuss_in_person', label: 'Prefer to discuss in person' },
];
const YES_NO_PREFER = [
  { value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }, { value: 'prefer_not_say', label: 'Prefer not to say' },
];
const FREQUENCY_OPTIONS = [
  { value: 'never', label: 'Never' }, { value: 'occasionally', label: 'Occasionally' },
  { value: 'frequently', label: 'Frequently' }, { value: 'almost_always', label: 'Almost always' },
];
const EMOTION_FREQ = [
  { value: 'never', label: 'Never' }, { value: 'sometimes', label: 'Sometimes' },
  { value: 'often', label: 'Often' }, { value: 'almost_always', label: 'Almost always' },
];
const PROVIDER_OPTIONS = [
  { value: 'gp', label: 'GP' }, { value: 'psychiatrist', label: 'Psychiatrist' },
  { value: 'psychologist', label: 'Psychologist' }, { value: 'ot', label: 'Occupational therapist' },
  { value: 'speech_path', label: 'Speech pathologist' }, { value: 'provider_other', label: 'Other' },
];
const SUBSTANCE_OPTIONS = [
  { value: 'sub_none', label: 'None / rarely' }, { value: 'alcohol', label: 'Alcohol' },
  { value: 'cannabis', label: 'Cannabis' }, { value: 'nicotine', label: 'Nicotine' },
  { value: 'stimulants', label: 'Stimulants' }, { value: 'sub_other', label: 'Other' },
  { value: 'sub_prefer_not', label: 'Prefer not to say' },
];

// ── Styles ─────────────────────────────────────────────────────────
const sectionStyle = { marginBottom: 32, paddingBottom: 16, borderBottom: '1px solid #e5e7eb' };
const sectionTitle = { fontSize: '1.25rem', fontWeight: 700, color: '#1e3a5f', marginBottom: 4 };
const optLabel = { fontSize: '0.8125rem', fontStyle: 'italic', color: '#9ca3af', marginLeft: 4 };
const fieldGap = { marginBottom: 16 };
const labelStyle = { fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: 6, display: 'block' };
const inputStyle = {
  width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db',
  fontSize: '0.875rem', fontFamily: 'inherit', color: '#1a202c',
};
const textareaStyle = { ...inputStyle, minHeight: 80, resize: 'vertical' };
const shortTextarea = { ...inputStyle, minHeight: 48, resize: 'vertical' };
const safetyNote = { fontSize: '0.8125rem', color: '#6b7280', padding: '10px 14px', borderRadius: 8, backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', marginTop: 8 };

// ── Component ──────────────────────────────────────────────────────
export default function NeuroDevPreAdultForm({ responses, setResponses }) {
  const r = responses;
  const set = useCallback((key, val) => setResponses(prev => ({ ...prev, [key]: val })), [setResponses]);

  return (
    <div style={{ maxWidth: 800 }}>

      {/* ─── Personal Information ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Personal Information</h2>
        <div style={{ display: 'flex', gap: 16, ...fieldGap }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>First name</label>
            <input type="text" style={inputStyle} value={r.first_name || ''} onChange={e => set('first_name', e.target.value)} data-testid="ndpa-first-name" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Last name / surname</label>
            <input type="text" style={inputStyle} value={r.last_name || ''} onChange={e => set('last_name', e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16, ...fieldGap }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Preferred name<span style={optLabel}>(if different)</span></label>
            <input type="text" style={inputStyle} value={r.preferred_name || ''} onChange={e => set('preferred_name', e.target.value)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Pronouns<span style={optLabel}>(optional)</span></label>
            <input type="text" style={inputStyle} value={r.pronouns || ''} onChange={e => set('pronouns', e.target.value)} placeholder="e.g. she/her, he/him, they/them" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 16, ...fieldGap }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Date of birth</label>
            <DayFirstDate label="Date of birth" notFuture inputStyle={inputStyle} value={r.dob || ''} onChange={v => set('dob', v)} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Today's date</label>
            <DayFirstDate label="Date" inputStyle={inputStyle} value={r.form_date || ''} onChange={v => set('form_date', v)} />
          </div>
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Gender" options={GENDER_OPTIONS} value={r.gender || ''} onChange={v => set('gender', v)}
            revealField={{ value: 'other', placeholder: "Please specify..." }} revealValue={r.gender_other} onRevealChange={v => set('gender_other', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Relationship to person being assessed (e.g. Self, Partner, Parent)</label>
          <input type="text" style={inputStyle} value={r.relationship || ''} onChange={e => set('relationship', e.target.value)} />
        </div>
      </div>

      {/* ─── About You ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>About You</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>What are your strengths? What do you enjoy, and what do others value about you?</label>
          <textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.early_strengths || ''} onChange={e => set('early_strengths', e.target.value)} />
        </div>
      </div>

      {/* ─── Household & Living Situation (NEW) ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Household and Living Situation</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Who do you live with?</label>
          <textarea style={shortTextarea} value={r.household || ''} onChange={e => set('household', e.target.value)} placeholder="e.g. Partner, two children, housemates, alone" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Relationship status<span style={optLabel}>(optional)</span></label>
          <input type="text" style={inputStyle} value={r.relationship_status || ''} onChange={e => set('relationship_status', e.target.value)} placeholder="e.g. Partnered, single, married" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Dependents<span style={optLabel}>(optional)</span></label>
          <textarea style={shortTextarea} value={r.dependents || ''} onChange={e => set('dependents', e.target.value)} placeholder="e.g. Two children (ages 4 and 7), elderly parent" />
        </div>
      </div>

      {/* ─── Early Development & History ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Early Development and History</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Pregnancy and birth complications</label>
          <textarea style={textareaStyle} value={r.pregnancy_birth || ''} onChange={e => set('pregnancy_birth', e.target.value)} placeholder="Were there any complications during your mother's pregnancy or your birth? If known" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Developmental milestones</label>
          <textarea style={textareaStyle} value={r.milestones || ''} onChange={e => set('milestones', e.target.value)} placeholder="Were any developmental milestones delayed (e.g., late to walk, late to talk)? If known" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Did you experience any loss or decline in skills after they had already developed?" options={YES_NO_UNSURE} value={r.skill_regression || ''} onChange={v => set('skill_regression', v)} />
        </div>
        {r.skill_regression === 'yes' && (
          <div style={fieldGap}>
            <label style={labelStyle}>Please describe</label>
            <textarea style={textareaStyle} value={r.skill_regression_details || ''} onChange={e => set('skill_regression_details', e.target.value)} />
          </div>
        )}
        <div style={fieldGap}>
          <ChipSelector label="Did you receive speech and language therapy as a child?" options={YES_NO_UNSURE} value={r.speech_therapy || ''} onChange={v => set('speech_therapy', v)} />
        </div>
        {r.speech_therapy === 'yes' && (
          <div style={fieldGap}>
            <label style={labelStyle}>Details</label>
            <textarea style={textareaStyle} value={r.speech_therapy_details || ''} onChange={e => set('speech_therapy_details', e.target.value)} />
          </div>
        )}
        <div style={fieldGap}>
          <ChipSelector label="Did you have contact with early intervention services, child development centres, or disability services?" options={YES_NO_UNSURE} value={r.early_intervention || ''} onChange={v => set('early_intervention', v)} />
        </div>
        {r.early_intervention === 'yes' && (
          <div style={fieldGap}>
            <label style={labelStyle}>Details</label>
            <textarea style={textareaStyle} value={r.early_intervention_details || ''} onChange={e => set('early_intervention_details', e.target.value)} />
          </div>
        )}
      </div>

      {/* ─── Previous Support & Diagnosis ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Previous Support and Diagnosis</h2>
        <div style={fieldGap}>
          <ChipSelector label="Have you been previously assessed or diagnosed for any neurodevelopmental or mental health condition?" options={YES_NO_UNSURE} value={r.previous_diagnosis || ''} onChange={v => set('previous_diagnosis', v)} />
        </div>
        {r.previous_diagnosis === 'yes' && (
          <div style={fieldGap}>
            <label style={labelStyle}>Diagnosis details</label>
            <textarea style={textareaStyle} value={r.previous_diagnosis_details || ''} onChange={e => set('previous_diagnosis_details', e.target.value)} placeholder="What was assessed/diagnosed? By whom? When?" />
          </div>
        )}
        <div style={fieldGap}>
          <ChipSelector label="Are there previous assessment reports available?" options={YES_NO} value={r.reports_available || ''} onChange={v => set('reports_available', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Family history</label>
          <textarea style={textareaStyle} value={r.family_history || ''} onChange={e => set('family_history', e.target.value)} placeholder="Is there a history of neurodivergence (Autism, ADHD, Learning Differences, Tics) or mental health conditions in your family?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Previous interventions</label>
          <textarea style={textareaStyle} value={r.previous_interventions || ''} onChange={e => set('previous_interventions', e.target.value)} placeholder="Any previous therapy (CBT, counselling, OT, speech), coaching, or other interventions and how they helped" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Consent to contact other providers" options={PROVIDER_OPTIONS} value={r.provider_consent || []} onChange={v => set('provider_consent', v)} multi />
        </div>
        {(r.provider_consent || []).length > 0 && (
          <div style={fieldGap}>
            <label style={labelStyle}>Provider names and contact details</label>
            <textarea style={textareaStyle} value={r.provider_details || ''} onChange={e => set('provider_details', e.target.value)} placeholder="Please list names and contact details for each provider" />
          </div>
        )}
      </div>

      {/* ─── Education & Work ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Education and Work History</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Educational background</label>
          <textarea style={textareaStyle} value={r.education_background || ''} onChange={e => set('education_background', e.target.value)} placeholder="Highest level completed, any difficulties or strengths in specific subjects" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Did you receive extra support or accommodations at school?" options={YES_NO_UNSURE} value={r.school_support || ''} onChange={v => set('school_support', v)} />
        </div>
        {r.school_support === 'yes' && (
          <div style={fieldGap}>
            <label style={labelStyle}>Support details</label>
            <textarea style={textareaStyle} value={r.school_support_details || ''} onChange={e => set('school_support_details', e.target.value)} />
          </div>
        )}
        <div style={fieldGap}>
          <label style={labelStyle}>Current work or study situation</label>
          <textarea style={textareaStyle} value={r.current_work || ''} onChange={e => set('current_work', e.target.value)} placeholder="What challenges do you face?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Employment or study difficulties</label>
          <textarea style={textareaStyle} value={r.work_difficulties || ''} onChange={e => set('work_difficulties', e.target.value)} placeholder="Difficulties keeping jobs, completing courses, managing work/study demands?" />
        </div>
      </div>

      {/* ─── Social Communication (TRIMMED: merged fields) ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Social Communication and Connection</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Tell us about how you communicate and connect with others</label>
          <textarea style={textareaStyle} value={r.communication_connection || ''} onChange={e => set('communication_connection', e.target.value)} placeholder="How do you use facial expressions, eye contact, and gestures? How do you interpret sarcasm, jokes, or implied meaning? How are you with unfamiliar people?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Social interaction style</label>
          <textarea style={textareaStyle} value={r.social_style || ''} onChange={e => set('social_style', e.target.value)} placeholder="Do social interactions feel like performing? Do you prefer structured or unstructured social settings?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Relationships and friendships</label>
          <textarea style={textareaStyle} value={r.relationships || ''} onChange={e => set('relationships', e.target.value)} placeholder="How do you form and maintain friendships and close relationships? What role do you usually play?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Speech patterns</label>
          <textarea style={textareaStyle} value={r.speech_patterns || ''} onChange={e => set('speech_patterns', e.target.value)} placeholder="Have you been told you speak formally, use scripted phrases, or are too blunt?" />
        </div>
      </div>

      {/* ─── Attention & Executive Processing ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Attentional and Executive Processing</h2>
        <div style={fieldGap}>
          <ChipSelector label="How often do you have difficulty focusing on tasks, conversations, or activities?" options={FREQUENCY_OPTIONS} value={r.focus_difficulty || ''} onChange={v => set('focus_difficulty', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="How often do you feel internally restless, 'driven by a motor', or unable to relax?" options={FREQUENCY_OPTIONS} value={r.restlessness || ''} onChange={v => set('restlessness', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="How often do you act quickly on impulses, blurt out responses, or have difficulty waiting?" options={FREQUENCY_OPTIONS} value={r.impulsivity || ''} onChange={v => set('impulsivity', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Onset and settings</label>
          <textarea style={textareaStyle} value={r.attention_onset || ''} onChange={e => set('attention_onset', e.target.value)} placeholder="At what age did these differences first become noticeable? In which settings?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Organisation and executive function</label>
          <textarea style={textareaStyle} value={r.organisation || ''} onChange={e => set('organisation', e.target.value)} placeholder="Difficulties with organisation, losing things, forgetfulness, time management, switching between tasks" />
        </div>
      </div>

      {/* ─── Interests & Sensory ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Interests and Sensory Regulation</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Intense interests or hobbies</label>
          <textarea style={textareaStyle} value={r.intense_interests || ''} onChange={e => set('intense_interests', e.target.value)} placeholder="How deeply do you engage with them? How do others respond?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Routines, rituals, and response to unexpected changes</label>
          <textarea style={textareaStyle} value={r.routines_and_changes || ''} onChange={e => set('routines_and_changes', e.target.value)} placeholder="Do you have specific routines or rules you follow? How do you handle unexpected changes to plans?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Sensory responses</label>
          <textarea style={textareaStyle} value={r.sensory_responses || ''} onChange={e => set('sensory_responses', e.target.value)} placeholder="Responses to sounds, lights, textures, tastes, or smells. Do you seek or avoid particular sensory experiences?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Repetitive movements or self-soothing</label>
          <textarea style={textareaStyle} value={r.repetitive_movements || ''} onChange={e => set('repetitive_movements', e.target.value)} placeholder="e.g. leg bouncing, pen clicking, rocking, hair twirling, skin picking" />
        </div>
      </div>

      {/* ─── Emotional Regulation ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Emotional Regulation</h2>
        <div style={fieldGap}>
          <ChipSelector label="Do you have difficulty understanding or identifying your own emotions?" options={EMOTION_FREQ} value={r.emotion_identification || ''} onChange={v => set('emotion_identification', v)} />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Do you have difficulty managing or regulating your emotions once you become upset?" options={EMOTION_FREQ} value={r.emotion_regulation || ''} onChange={v => set('emotion_regulation', v)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>How do you typically experience and manage strong emotions?</label>
          <textarea style={textareaStyle} value={r.emotion_management || ''} onChange={e => set('emotion_management', e.target.value)} placeholder="What strategies do you use? Shutting down, withdrawal, irritability, intense crying?" />
        </div>
      </div>

      {/* ─── Well-being & Functional Impact ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Well-being and Functional Impact</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Medical history</label>
          <textarea style={textareaStyle} value={r.medical_history || ''} onChange={e => set('medical_history', e.target.value)} placeholder="Vision, hearing, seizures, tics, head injuries, chronic conditions?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Sleep</label>
          <textarea style={textareaStyle} value={r.sleep || ''} onChange={e => set('sleep', e.target.value)} placeholder="Difficulties falling asleep, staying asleep, waking early, excessive tiredness?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Mental health</label>
          <textarea style={textareaStyle} value={r.mental_health || ''} onChange={e => set('mental_health', e.target.value)} placeholder="Anxiety, low mood, burnout, emotional exhaustion?" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Current medications</label>
          <textarea style={shortTextarea} value={r.medications || ''} onChange={e => set('medications', e.target.value)} placeholder="List any current medications" />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Daily functioning impact</label>
          <textarea style={textareaStyle} value={r.daily_impact || ''} onChange={e => set('daily_impact', e.target.value)} placeholder="How do these challenges affect your daily functioning, relationships, and sense of well-being?" />
        </div>
      </div>

      {/* ─── Safety & Wellbeing ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Safety and Wellbeing</h2>
        <div style={fieldGap}>
          <ChipSelector label="Have you ever engaged in self-harming behaviours or experienced thoughts of harming yourself?" options={YES_NO_PREFER} value={r.self_harm || ''} onChange={v => set('self_harm', v)} />
        </div>
        {r.self_harm === 'yes' && (
          <div style={{ ...fieldGap, padding: 12, borderRadius: 8, border: '1px solid #fca5a5', backgroundColor: '#fef2f2' }}>
            <label style={{ ...labelStyle, color: '#991b1b' }}>Please provide details</label>
            <textarea style={{ ...textareaStyle, borderColor: '#fca5a5' }} value={r.self_harm_details || ''} onChange={e => set('self_harm_details', e.target.value)} placeholder="This information will be handled with care" />
          </div>
        )}
        {r.self_harm === 'prefer_not_say' && (
          <div style={safetyNote}>
            We understand. This will be discussed sensitively in your first session. If you are in crisis, please contact <strong>Lifeline on 13 11 14</strong> or <strong>Beyond Blue on 1300 22 4636</strong>.
          </div>
        )}
        <div style={fieldGap}>
          <label style={labelStyle}>Screen time</label>
          <textarea style={shortTextarea} value={r.screen_time || ''} onChange={e => set('screen_time', e.target.value)} placeholder="Approximate daily hours, and any difficulty stopping or transitioning off screens" />
        </div>
        <div style={fieldGap}>
          <ChipSelector label="Do you use any of the following regularly?" options={SUBSTANCE_OPTIONS} value={r.substance_use || []} onChange={v => set('substance_use', v)} multi />
        </div>
        {(r.substance_use || []).some(v => !['sub_none', 'sub_prefer_not'].includes(v)) && (
          <div style={fieldGap}>
            <label style={labelStyle}>Brief details<span style={optLabel}>(frequency, amount: this helps with differential diagnosis)</span></label>
            <textarea style={shortTextarea} value={r.substance_details || ''} onChange={e => set('substance_details', e.target.value)} />
          </div>
        )}
      </div>

      {/* ─── Strengths ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Strengths and Identity</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>What are you good at? What do others value about you?</label>
          <textarea style={textareaStyle} value={r.strengths || ''} onChange={e => set('strengths', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>When do you feel most like yourself, or most settled and at ease?</label>
          <textarea style={textareaStyle} value={r.happiest_settled || ''} onChange={e => set('happiest_settled', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>How would you describe your confidence and self-esteem?</label>
          <textarea style={textareaStyle} value={r.confidence_self_esteem || ''} onChange={e => set('confidence_self_esteem', e.target.value)} />
        </div>
      </div>

      {/* ─── Significant Life Events ─── */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>Significant Life Events</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>Please share any significant life events that may be relevant</label>
          <textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.significant_life_events || ''} onChange={e => set('significant_life_events', e.target.value)} />
        </div>
      </div>

      {/* ─── Summary & Priorities ─── */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={sectionTitle}>Your Concerns and Goals</h2>
        <div style={fieldGap}>
          <label style={labelStyle}>What are your main concerns or reasons for seeking this assessment?</label>
          <textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.main_concerns || ''} onChange={e => set('main_concerns', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>What are you hoping this assessment will help clarify or achieve?</label>
          <textarea style={{ ...textareaStyle, minHeight: 100 }} value={r.assessment_hopes || ''} onChange={e => set('assessment_hopes', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>What would be most helpful for us to know about how you are travelling right now?<span style={optLabel}>(optional)</span></label>
          <textarea style={textareaStyle} value={r.personal_wellbeing || ''} onChange={e => set('personal_wellbeing', e.target.value)} />
        </div>
        <div style={fieldGap}>
          <label style={labelStyle}>Is there anything else you feel we should know?</label>
          <textarea style={textareaStyle} value={r.anything_else || ''} onChange={e => set('anything_else', e.target.value)} />
        </div>
      </div>
    </div>
  );
}
